import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ariaSortFor, type TagMappingSort } from './sort'

interface Props {
  label: string
  /**
   * The key the server sorts this column by. Absent means the column cannot be
   * sorted, and the header renders as plain text with no affordance — a header
   * that looks sortable and is not is the exact defect F09 reported.
   */
  sortKey?: string
  sort: TagMappingSort | null
  onSort?: (key: string) => void
  className?: string
}

/**
 * A `<th>` of the Tag mapping tables, sortable when the server can order by it.
 *
 * Drawn the way the Data Catalog draws its headers (`CatalogTableV2.SortIcon`):
 * no glyph at rest, a single chevron on the SORTED column only — up for
 * ascending, down for descending — and the label brightens on hover. The
 * review asked for exactly that ("follow what we have on data catalog", F09),
 * and the two tables sit one click apart in the Data section, so they must
 * read the same. The control is a real button: keyboard-operable, and the
 * `aria-sort` on the cell is what announces the active direction.
 */
const SortableHeader = ({ label, sortKey, sort, onSort, className }: Props) => {
  const sortable = !!sortKey && !!onSort
  const ariaSort = sortKey ? ariaSortFor(sort, sortKey) : undefined

  return (
    <th scope="col" aria-sort={ariaSort} className={cn('px-6 py-3 font-normal', className)}>
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className="inline-flex cursor-pointer select-none items-center gap-1 rounded-sm text-start hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span>{label}</span>
          {ariaSort === 'ascending' && (
            <ChevronUp aria-hidden data-testid="sort-asc" className="size-3.5 text-foreground" />
          )}
          {ariaSort === 'descending' && (
            <ChevronDown aria-hidden data-testid="sort-desc" className="size-3.5 text-foreground" />
          )}
        </button>
      ) : (
        label
      )}
    </th>
  )
}

export default SortableHeader
